import cx from 'classnames';
import * as React from 'react';
import { callAll } from '../helper/fp';
import { useId } from '../helper/hooks';
import { Tooltip } from '../Tooltip/Tooltip';

type DivProps = Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'ref'>;
type LabelProps = Omit<React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLLabelElement>, HTMLLabelElement>, 'ref'>;

interface ISelectableBoxBaseProps {
  /** callback to be invoked when the box is selected. use this instead of onClick as it handle keyboard as well */
  onSelect?: () => void;
  /** indicate if the box is currently selected */
  active?: boolean;
  headerText?: React.ReactNode;
  labelText?: React.ReactNode;
  /** the box is disabled and cannot be selected */
  disabled?: boolean;
  /** shadow around the box */
  shadow?: boolean;
  /** to add checkbox in the box */
  withCheckbox?: boolean;
  tooltip?: React.ReactNode;
}

interface ISelectableDivProps extends DivProps, ISelectableBoxBaseProps {
  /** callback to be invoked when the box is selected. use this instead of onClick as it handle keyboard as well */
  onSelect?: () => void;
}

interface ISelectableCheckboxProps extends LabelProps, ISelectableBoxBaseProps {
  /** callback to be invoked when the box is selected. use this instead of onClick as it handle keyboard as well */
  onSelect?: () => void;
  /** to add checkbox in the box */
  withCheckbox: true;
}

type ISelectableBoxProps = ISelectableDivProps | ISelectableCheckboxProps;
/**
 * `SelectableBox` is button-like boxes that user can select by clicking or press enter.
 */
export const SelectableBox: React.FunctionComponent<ISelectableBoxProps> = (props) => {
  const { headerText, labelText, className, active, withCheckbox, disabled, shadow, children } = props;
  const classes = cx(
    'Box',
    withCheckbox ? 'CheckBox' : 'Box--selectable',
    active && 'is-active',
    disabled && 'Box--disabled',
    shadow && !withCheckbox && 'Box--shadow',
    className
  );

  const childrenNode = (
    <div>
      {labelText && <span className="Box-label">{labelText}</span>}
      {headerText && <h4 className="Box-header">{headerText}</h4>}
      {children}
    </div>
  );

  return withCheckbox ? (
    <SelectableCheckBox {...(props as ISelectableCheckboxProps)} className={classes}>
      {childrenNode}
    </SelectableCheckBox>
  ) : (
    <SelectableDivBox {...(props as ISelectableDivProps)} className={classes}>
      {childrenNode}
    </SelectableDivBox>
  );
};

const SelectableCheckBox: React.FC<ISelectableCheckboxProps> = ({
  headerText,
  labelText,
  className,
  active,
  withCheckbox,
  disabled,
  shadow,
  children,
  onSelect,
  onKeyUp,
  tabIndex = 0,
  role = 'button',
  onKeyDown,
  tooltip,
  id,
  ...divProps
}) => {
  const uniqueId = useId(id);

  const boxNode = (
    <label
      className={className}
      role={role}
      tabIndex={disabled ? -1 : tabIndex}
      onKeyDown={
        disabled
          ? onKeyDown
          : callAll(
              onKeyDown,
              onSelect &&
                ((ev) => {
                  if (ev.which === 13 || ev.which === 32) {
                    // enter and space key
                    onSelect();
                  }
                })
            )
      }
      htmlFor={uniqueId}
      {...divProps}
    >
      {children}
    </label>
  );

  return (
    <>
      <input
        onChange={onSelect}
        className="visuallyhidden"
        type="checkbox"
        id={uniqueId}
        aria-checked={active}
        checked={active}
        disabled={disabled}
      />
      {tooltip ? <Tooltip label={tooltip}>{boxNode}</Tooltip> : boxNode}
    </>
  );
};

const SelectableDivBox: React.FC<ISelectableDivProps> = ({
  headerText,
  labelText,
  className,
  active,
  withCheckbox,
  disabled,
  shadow,
  children,
  onClick,
  onSelect,
  onKeyUp,
  tabIndex = 0,
  role = 'button',
  onKeyDown,
  tooltip,
  ...divProps
}) => {
  const boxNode = (
    <div
      className={className}
      role={role}
      tabIndex={disabled ? -1 : tabIndex}
      onClick={disabled ? undefined : callAll(onClick, onSelect)}
      onKeyDown={
        disabled
          ? undefined
          : callAll(
              onKeyDown,
              onSelect &&
                ((ev) => {
                  if (ev.which === 13 || ev.which === 32) {
                    // enter and space key
                    onSelect();
                  }
                })
            )
      }
      {...divProps}
    >
      {children}
    </div>
  );

  return <>{tooltip ? <Tooltip label={tooltip}>{boxNode}</Tooltip> : boxNode}</>;
};
